import React from "react";
import { Button, Card, Form, Collapse } from "react-bootstrap";
import { FiTrash2, FiChevronDown } from "react-icons/fi";
import { FaGripVertical } from "react-icons/fa";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { Part, DeleteTarget, SUBTASK_TYPE } from "./ProjectConfigurationTypes";
import TaskComponent from "./TaskComponent";

interface PartComponentProps {
  part: Part;
  phaseIndex: number;
  partIndex: number;
  partId: string;
  phaseId: string;
  isExpanded: boolean;
  projectId: string;
  subjects: any[];
  topicsCache: Record<string, any[]>;
  contentStatus?: {
    [SUBTASK_TYPE.VIDEO]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    [SUBTASK_TYPE.NOTES]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    [SUBTASK_TYPE.MCQ]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    [SUBTASK_TYPE.CODING]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    [SUBTASK_TYPE.PROJECT_CODING]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
  } | null;
  dragHandleProps: any;
  onToggleExpand: () => void;
  onNameChange: (name: string) => void;
  allParts?: Part[];
  onAddTask: () => void;
  onDelete: (target: DeleteTarget) => void;
  onTaskDragEnd: (result: DropResult) => void;
  onTaskToggleExpand: (taskId: string) => void;
  onTaskNameChange: (taskIndex: number, name: string) => void;
  onAddSubtask: (taskIndex: number) => void;
  onTaskDelete: (target: DeleteTarget) => void;
  onSubtaskDragEnd: (taskIndex: number, result: DropResult) => void;
  onSelectOrAssign: (taskIndex: number, subtaskIndex: number, actionType: "select" | "assign") => void;
  onSubtaskNameChange: (taskIndex: number, subtaskIndex: number, name: string) => void;
  onMandatoryChange: (taskIndex: number, subtaskIndex: number, checked: boolean) => void;
  onSubjectChange: (taskIndex: number, subtaskIndex: number, subjectId: string) => void;
  onTopicChange: (taskIndex: number, subtaskIndex: number, topicId: string) => void;
  onSubtopicChange: (taskIndex: number, subtaskIndex: number, subtopicId: string) => void;
  onSubtaskTypeChange: (taskIndex: number, subtaskIndex: number, subtaskType: string) => void;
  onEditorChange: (taskIndex: number, subtaskIndex: number, editor: string) => void;
  onLevelChange: (taskIndex: number, subtaskIndex: number, level: string) => void;
  onContentLevelChange: (taskIndex: number, subtaskIndex: number, level: string) => void;
  onContentCountChange: (taskIndex: number, subtaskIndex: number, level: string, count: string) => void;
  onAddContent: (taskIndex: number, subtaskIndex: number) => void;
  onFetchContent: (taskIndex: number, subtaskIndex: number, subtopicId: string) => Promise<void>;
  onContentSelected: (taskIndex: number, subtaskIndex: number, contentId: string, level: string, time: string) => void;
  onPreviewContent: (taskIndex: number, subtaskIndex: number, type: typeof SUBTASK_TYPE.VIDEO | typeof SUBTASK_TYPE.NOTES, content: any) => void;
  onContentCreated: (taskIndex: number, subtaskIndex: number, level1: string[], level2: string[], level3: string[]) => void;
  onSubtaskDelete: (target: DeleteTarget) => void;
  onFetchTopics: (subjectId: string) => Promise<void>;
  onDescriptionChange?: (taskIndex: number, subtaskIndex: number, description: string) => void;
  onCcChange?: (taskIndex: number, subtaskIndex: number, ccId: string) => void;
  assignedCcIds?: string[] | null;
  generateId: (prefix: string, index: number) => string;
  isTaskExpanded: (taskId: string) => boolean;
  isSubtaskExpanded: (subtaskId: string) => boolean;
  toggleSubtaskExpand: (subtaskId: string) => void;
  isPartRestricted?: (phaseId: string, partId: string) => boolean;
  isTaskRestricted?: (phaseId: string, partId: string, taskId: string) => boolean;
  isSubtaskRestricted?: (phaseId: string, partId: string, taskId: string, subtaskId: string) => boolean;
}

/**
 * PartComponent: Renders a part card with drag-and-drop support for tasks
 * This component displays a single part within a phase and manages its tasks.
 * It provides functionality for:
 * - Expanding/collapsing the part
 * - Editing the part name
 * - Adding new tasks
 * - Deleting the part
 * - Drag-and-drop reordering of tasks
 * - Rendering TaskComponent children for each task
 */
const PartComponent: React.FC<PartComponentProps> = ({
  part,
  phaseIndex,
  partIndex,
  partId,
  phaseId,
  isExpanded,
  projectId,
  subjects,
  topicsCache,
  contentStatus,
  dragHandleProps,
  onToggleExpand,
  onNameChange,
  onAddTask,
  onDelete,
  onTaskDragEnd,
  onTaskToggleExpand,
  onTaskNameChange,
  onAddSubtask,
  onTaskDelete,
  onSubtaskDragEnd,
  onSelectOrAssign,
  onSubtaskNameChange,
  onMandatoryChange,
  onSubjectChange,
  onTopicChange,
  onSubtopicChange,
  onSubtaskTypeChange,
  onEditorChange,
  onLevelChange,
  onContentLevelChange,
  onContentCountChange,
  onAddContent,
  onFetchContent,
  onContentSelected,
  onPreviewContent,
  onContentCreated,
  onSubtaskDelete,
  onFetchTopics,
  generateId,
  isTaskExpanded,
  isSubtaskExpanded,
  toggleSubtaskExpand,
  onDescriptionChange,
  onCcChange,
  assignedCcIds,
  isPartRestricted,
  isTaskRestricted,
  isSubtaskRestricted,
  allParts = []
}) => {
  const partRestricted = isPartRestricted ? isPartRestricted(phaseId, part.id) : false;
  
  // Check for duplicate part names within the phase
  const isDuplicatePartName = part.name && part.name.trim() !== "" && 
    allParts.some((p, idx) => idx !== partIndex && p.name && p.name.trim().toLowerCase() === part.name.trim().toLowerCase());
  return (
    <Card className="mb-2">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          <div {...dragHandleProps} style={{ cursor: partRestricted ? 'not-allowed' : 'grab' }}>
            <FaGripVertical />
          </div>
          <Button
            variant="light"
            size="sm"
            className="p-1"
            onClick={onToggleExpand}
          >
            <FiChevronDown
              size={18}
              style={{
                transition: "transform 0.2s",
                transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)"
              }}
            />
          </Button>
          <span className="fw-bold me-2">{part.id ? part.id.charAt(0).toUpperCase() + part.id.slice(1) : part.id}</span>
          <div>
            <Form.Control
              type="text"
              placeholder="Part Name"
              value={part.name}
              onChange={(e) => onNameChange(e.target.value)}
              style={{ width: "200px" }}
            />
            {isDuplicatePartName && (
              <div className="text-warning mt-2">                Part name has already been used
              </div>
            )}
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-success"
            size="sm"
            onClick={onAddTask}
            disabled={partRestricted}
          >
            + Task
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => onDelete({
              type: "part",
              id: part.id,
              name: part.name || `Part ${partIndex + 1}`,
              phaseIndex,
              partIndex
            })}
            disabled={partRestricted}
          >
            <FiTrash2 size={16} />
          </Button>
        </div>
      </Card.Header>
      <Collapse in={isExpanded}>
        <Card.Body>
          <DragDropContext onDragEnd={onTaskDragEnd}>
            <Droppable droppableId={`tasks-${partId}`}>
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {part.tasks.map((task, taskIndex) => {
                    const taskId = `${partId}-${task.id}`;
                    return (
                      <Draggable 
                        key={task.id} 
                        draggableId={task.id} 
                        index={taskIndex}
                        isDragDisabled={isTaskRestricted ? isTaskRestricted(phaseId, part.id, task.id) : false}
                      >
                        {(provided, snapshot) => {
                          const taskRestricted = isTaskRestricted ? isTaskRestricted(phaseId, part.id, task.id) : false;
                          // Only apply opacity if task is restricted AND part is NOT restricted (to avoid compounding)
                          const shouldApplyOpacity = taskRestricted && !partRestricted;
                          return (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                ...provided.draggableProps.style,
                                backgroundColor: snapshot.isDragging ? '#e3f2fd' : 'transparent',
                                opacity: shouldApplyOpacity ? 0.6 : 1
                              }}
                            >
                              <TaskComponent
                                task={task}
                                phaseIndex={phaseIndex}
                                partIndex={partIndex}
                                taskIndex={taskIndex}
                                taskId={taskId}
                                phaseId={phaseId}
                                partId={part.id}
                                isExpanded={isTaskExpanded(taskId)}
                                projectId={projectId}
                                subjects={subjects}
                                topicsCache={topicsCache}
                                contentStatus={contentStatus}
                                dragHandleProps={provided.dragHandleProps}
                                onToggleExpand={() => onTaskToggleExpand(taskId)}
                                onNameChange={(name) => onTaskNameChange(taskIndex, name)}
                                onAddSubtask={() => onAddSubtask(taskIndex)}
                                onDelete={onTaskDelete}
                                onSubtaskDragEnd={(result) => onSubtaskDragEnd(taskIndex, result)}
                                onSelectOrAssign={(subtaskIndex, actionType) => onSelectOrAssign(taskIndex, subtaskIndex, actionType)}
                                onSubtaskNameChange={(subtaskIndex, name) => onSubtaskNameChange(taskIndex, subtaskIndex, name)}
                                onMandatoryChange={(subtaskIndex, checked) => onMandatoryChange(taskIndex, subtaskIndex, checked)}
                                onSubjectChange={(subtaskIndex, subjectId) => onSubjectChange(taskIndex, subtaskIndex, subjectId)}
                                onTopicChange={(subtaskIndex, topicId) => onTopicChange(taskIndex, subtaskIndex, topicId)}
                                onSubtopicChange={(subtaskIndex, subtopicId) => onSubtopicChange(taskIndex, subtaskIndex, subtopicId)}
                                onSubtaskTypeChange={(subtaskIndex, subtaskType) => onSubtaskTypeChange(taskIndex, subtaskIndex, subtaskType)}
                                onEditorChange={(subtaskIndex, editor) => onEditorChange(taskIndex, subtaskIndex, editor)}
                                onLevelChange={(subtaskIndex, level) => onLevelChange(taskIndex, subtaskIndex, level)}
                                onContentLevelChange={(subtaskIndex, level) => onContentLevelChange(taskIndex, subtaskIndex, level)}
                                onContentCountChange={(subtaskIndex, level, count) => onContentCountChange(taskIndex, subtaskIndex, level, count)}
                                onAddContent={(subtaskIndex) => onAddContent(taskIndex, subtaskIndex)}
                                onFetchContent={(subtaskIndex, subtopicId) => onFetchContent(taskIndex, subtaskIndex, subtopicId)}
                                onContentSelected={(subtaskIndex, contentId, level, time) => onContentSelected(taskIndex, subtaskIndex, contentId, level, time)}
                                onPreviewContent={(subtaskIndex, type, content) => onPreviewContent(taskIndex, subtaskIndex, type, content)}
                                onContentCreated={(subtaskIndex, level1, level2, level3) => onContentCreated(taskIndex, subtaskIndex, level1, level2, level3)}
                                onSubtaskDelete={onSubtaskDelete}
                                onFetchTopics={onFetchTopics}
                                generateId={generateId}
                                isSubtaskExpanded={isSubtaskExpanded}
                                toggleSubtaskExpand={toggleSubtaskExpand}
                                onDescriptionChange={onDescriptionChange ? (subtaskIndex, description) => onDescriptionChange(taskIndex, subtaskIndex, description) : undefined}
                                onCcChange={onCcChange ? (subtaskIndex, ccId) => onCcChange(taskIndex, subtaskIndex, ccId) : undefined}
                                assignedCcIds={assignedCcIds}
                                isTaskRestricted={isTaskRestricted}
                                isSubtaskRestricted={isSubtaskRestricted}
                                allTasks={part.tasks}
                              />
                            </div>
                            );
                          }}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Card.Body>
      </Collapse>
    </Card>
  );
};

export default PartComponent;

